import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const PROPERTY = '_medio_pago';
const TRANSFER_VALUE = 'transferencia';

/* Every pass settles one line, so this only guards against a cart that keeps growing mid-flight. */
const MAX_LINE_PASSES = 60;
const RESTORE_ATTEMPTS = 3;

/**
 * Lets the customer pick card or bank transfer before entering checkout, so the discount is already
 * applied by the time the payment screen renders.
 *
 * @extends {Component<{ cardButton: HTMLButtonElement, transferButton: HTMLButtonElement, cardPrice?: HTMLElement, transferPrice?: HTMLElement, errorMessage?: HTMLElement }>}
 */
class PaymentChoiceComponent extends Component {
  requiredRefs = ['cardButton', 'transferButton'];

  #abortController = new AbortController();
  #pending = false;

  connectedCallback() {
    super.connectedCallback();

    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleCard = (event) => {
    event.preventDefault();

    return this.#run(this.refs.cardButton, async () => {
      if (this.#isProductContext) await this.#addToCart(null);

      await this.#clearEveryLine();
      await this.#dropTransferDiscount();

      window.location.assign(this.#checkoutUrl);
    });
  };

  handleTransfer = (event) => {
    event.preventDefault();

    return this.#run(this.refs.transferButton, async () => {
      if (this.#isProductContext) await this.#addToCart(TRANSFER_VALUE);

      await this.#markEveryLine(TRANSFER_VALUE);

      const code = encodeURIComponent(this.#discountCode);
      window.location.assign(`/discount/${code}?redirect=${encodeURIComponent(this.#checkoutUrl)}`);
    });
  };

  get #isProductContext() {
    return this.dataset.context === 'product';
  }

  get #checkoutUrl() {
    return this.dataset.checkoutUrl || '/checkout';
  }

  get #discountCode() {
    return this.dataset.discountCode ?? '';
  }

  get #form() {
    const selector = this.dataset.formSelector;
    const form = selector ? document.querySelector(selector) : this.closest('form');

    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * Keeps both buttons disabled while the request chain runs, so a second click cannot start a
   * parallel flow. On success we navigate away, so the buttons are deliberately left disabled.
   *
   * @param {HTMLButtonElement} button
   * @param {() => Promise<void>} action
   */
  async #run(button, action) {
    if (this.#pending) return;

    const { cardButton, transferButton, errorMessage } = this.refs;

    this.#pending = true;
    cardButton.disabled = true;
    transferButton.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (errorMessage) errorMessage.hidden = true;

    try {
      await action();
    } catch (error) {
      console.error(error);

      this.#pending = false;
      cardButton.disabled = false;
      transferButton.disabled = false;
      button.removeAttribute('aria-busy');
      if (errorMessage) errorMessage.hidden = false;
    }
  }

  /**
   * @param {string | null} paymentMethod
   */
  async #addToCart(paymentMethod) {
    const form = this.#form;
    const body = form ? new FormData(form) : new FormData();

    if (!form) {
      const variantId = this.dataset.variantId;
      if (!variantId) throw new Error('payment-choice: no product form and no variant id available');

      body.set('id', variantId);
      body.set('quantity', '1');
    }

    if (paymentMethod) body.set(`properties[${PROPERTY}]`, paymentMethod);

    const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', body });

    if (!response.ok) throw new Error(`payment-choice: cart/add responded ${response.status}`);
  }

  /**
   * Writes the payment method property on every cart line.
   *
   * A line's key is derived from its properties, so a changed line comes back under a new key and
   * can merge into another line. That rules out walking a fixed list of keys: instead we re-read the
   * cart on each pass and track which keys are already settled.
   *
   * @param {string} value
   */
  async #markEveryLine(value) {
    const settled = new Set();

    for (let pass = 0; pass < MAX_LINE_PASSES; pass++) {
      const cart = await this.#fetchCart();
      const item = cart.items.find((/** @type {any} */ line) => !settled.has(line.key));

      if (!item) return;

      settled.add(item.key);

      const keysBefore = new Set(cart.items.map((/** @type {any} */ line) => line.key));
      const properties = { ...(item.properties ?? {}), [PROPERTY]: value };
      const updated = await this.#changeLine(item.key, item.quantity, properties);

      for (const line of updated.items) {
        if (!keysBefore.has(line.key)) settled.add(line.key);
      }
    }

    throw new Error('payment-choice: could not mark every cart line');
  }

  /**
   * Removes the payment method property from every cart line.
   *
   * `/cart/change.js` cannot delete a property: writing an empty string leaves the key behind with a
   * blank value, and an empty property object is ignored outright. So each marked line is dropped and
   * re-added without the property, which merges it back into the matching clean line when there is
   * one. Dropping before re-adding keeps a failed retry from inflating the quantity.
   */
  async #clearEveryLine() {
    for (let pass = 0; pass < MAX_LINE_PASSES; pass++) {
      const cart = await this.#fetchCart();
      const item = cart.items.find((/** @type {any} */ line) => PROPERTY in (line.properties ?? {}));

      if (!item) return;

      const properties = { ...item.properties };
      delete properties[PROPERTY];

      await this.#changeLine(item.key, 0);
      await this.#restoreLine(item, properties);
    }

    throw new Error('payment-choice: could not clear every cart line');
  }

  /**
   * Re-adds a line that was dropped to shed its payment method property. A failure here would leave
   * the customer's cart short an item, so it is retried before giving up.
   *
   * @param {any} item
   * @param {Record<string, string>} properties
   */
  async #restoreLine(item, properties) {
    const payload = {
      id: item.variant_id,
      quantity: item.quantity,
      properties,
      ...(item.selling_plan_allocation?.selling_plan?.id
        ? { selling_plan: item.selling_plan_allocation.selling_plan.id }
        : {}),
    };

    for (let attempt = 0; attempt < RESTORE_ATTEMPTS; attempt++) {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) return;
    }

    throw new Error(`payment-choice: could not restore variant ${item.variant_id}`);
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });

    if (!response.ok) throw new Error(`payment-choice: cart.js responded ${response.status}`);

    return response.json();
  }

  /**
   * @param {string} key
   * @param {number} quantity
   * @param {Record<string, string>} [properties]
   */
  async #changeLine(key, quantity, properties) {
    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(properties ? { id: key, quantity, properties } : { id: key, quantity }),
    });

    if (!response.ok) throw new Error(`payment-choice: cart/change responded ${response.status}`);

    return response.json();
  }

  /**
   * Drops the transfer discount so a customer who changes their mind is not charged the transfer
   * price on a card order. Any other code the customer entered is preserved.
   */
  async #dropTransferDiscount() {
    const code = this.#discountCode.toLowerCase();
    if (!code) return;

    const cart = await this.#fetchCart();
    const codes = (cart.discount_codes ?? []).map((/** @type {any} */ discount) => discount.code);
    const remaining = codes.filter((/** @type {string} */ existing) => existing.toLowerCase() !== code);

    if (remaining.length === codes.length) return;

    await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: remaining.join(',') }),
    });
  }

  #handleVariantUpdate = (/** @type {any} */ event) => {
    const variant = event.detail?.resource;

    if (!this.#pending) {
      const available = Boolean(variant?.available);
      this.refs.cardButton.disabled = !available;
      this.refs.transferButton.disabled = !available;
    }

    if (variant?.id) this.#renderPrices(String(variant.id));
  };

  /**
   * @param {string} variantId
   */
  #renderPrices(variantId) {
    const { cardPrice, transferPrice } = this.refs;

    let prices;
    try {
      prices = JSON.parse(this.dataset.prices ?? '{}');
    } catch {
      return;
    }

    const entry = prices[variantId];
    if (!entry) return;

    this.dataset.variantId = variantId;
    if (cardPrice) cardPrice.textContent = entry.card;
    if (transferPrice) transferPrice.textContent = entry.transfer;
  }
}

if (!customElements.get('payment-choice-component')) {
  customElements.define('payment-choice-component', PaymentChoiceComponent);
}
